import { animate, group, query, style, transition, trigger } from '@angular/animations';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GruppeTier } from '../../../gruppe-tier/classes/gruppe-tier';
import { MessageService } from '../../../../services/message-service/message.service';
import { AppService } from '../../../../services/app/app.service';
import { ScanErgebnis } from '../../classes/scan-ergebnis';
import { EinstallenService } from '../einstallen.service';

type GewichtRow = {
    tier: GruppeTier;
    gewicht: number | null;
    hasExistingWeight: boolean;
};

@Component({
    selector: 'kiene-zugang-gewichte-dialog',
    templateUrl: './zugang-gewichte-dialog.component.html',
    styleUrls: ['./zugang-gewichte-dialog.component.scss'],
    animations: [
        trigger('mobileTabSlide', [
            transition(':increment', [
                group([
                    query(
                        ':leave',
                        [
                            style({
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                zIndex: 1,
                            }),
                            animate(
                                '280ms cubic-bezier(0.4, 0, 0.2, 1)',
                                style({ transform: 'translateX(-100%)', opacity: 0 }),
                            ),
                        ],
                        { optional: true },
                    ),
                    query(
                        ':enter',
                        [
                            style({ transform: 'translateX(100%)', opacity: 0, zIndex: 2 }),
                            animate(
                                '280ms cubic-bezier(0.4, 0, 0.2, 1)',
                                style({ transform: 'translateX(0)', opacity: 1 }),
                            ),
                        ],
                        { optional: true },
                    ),
                ]),
            ]),
            transition(':decrement', [
                group([
                    query(
                        ':leave',
                        [
                            style({
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                zIndex: 1,
                            }),
                            animate(
                                '280ms cubic-bezier(0.4, 0, 0.2, 1)',
                                style({ transform: 'translateX(100%)', opacity: 0 }),
                            ),
                        ],
                        { optional: true },
                    ),
                    query(
                        ':enter',
                        [
                            style({ transform: 'translateX(-100%)', opacity: 0, zIndex: 2 }),
                            animate(
                                '280ms cubic-bezier(0.4, 0, 0.2, 1)',
                                style({ transform: 'translateX(0)', opacity: 1 }),
                            ),
                        ],
                        { optional: true },
                    ),
                ]),
            ]),
        ]),
        trigger('mobileTabLabel', [
            transition(':increment', [
                style({ opacity: 0, transform: 'translateX(16px)' }),
                animate('220ms ease-out', style({ opacity: 1, transform: 'translateX(0)' })),
            ]),
            transition(':decrement', [
                style({ opacity: 0, transform: 'translateX(-16px)' }),
                animate('220ms ease-out', style({ opacity: 1, transform: 'translateX(0)' })),
            ]),
        ]),
    ],
})
export class ZugangGewichteDialogComponent implements OnInit {
    isLoading = true;
    isSaving = false;
    rows: GewichtRow[] = [];
    totalGewicht: number | null = null;
    gleichesGewicht: number | null = null;
    activeTabIndex = 0;
    readonly tabLabels = ['Gesamtgewicht', 'Einzeltiergewicht', 'Einzeltier'];
    private isSyncing = false;

    constructor(
        public dialogRef: MatDialogRef<ZugangGewichteDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            gruppeId: number;
            gsId?: number | null;
            onlyMissing?: boolean;
            scanErgebnisse?: ScanErgebnis[];
        },
        private readonly messageService: MessageService,
        private readonly einstallenService: EinstallenService,
        private readonly appService: AppService,
    ) {}

    get isMobileScreen(): boolean {
        return this.appService.isMobileScreen;
    }

    get activeTabLabel(): string {
        return this.tabLabels[this.activeTabIndex] || this.tabLabels[0];
    }

    previousTab(): void {
        if (this.activeTabIndex > 0) {
            this.onTabIndexChanged(this.activeTabIndex - 1);
        }
    }

    nextTab(): void {
        if (this.activeTabIndex < this.tabLabels.length - 1) {
            this.onTabIndexChanged(this.activeTabIndex + 1);
        }
    }

    ngOnInit(): void {
        if (this.isMobileScreen) {
            this.dialogRef.addPanelClass('gewicht-dialog-panel--mobile');
            this.dialogRef.updateSize('calc(100vw - 24px)', undefined);
        }
        this.loadRowsFromScans(this.data?.scanErgebnisse || []);
    }

    onRowGewichtChanged(): void {
        if (this.isSyncing) {
            return;
        }
        this.syncAggregatesFromRows();
    }

    onTotalGewichtChanged(): void {
        // Nur Vorschau (calculatedWeightFromTotal) – Verteilung erst beim Speichern oder Tab-Wechsel.
    }

    onTabIndexChanged(index: number): void {
        const previous = this.activeTabIndex;
        if (
            previous === 0 &&
            index !== 0 &&
            this.totalGewicht &&
            this.totalGewicht > 0 &&
            this.editableRowCount > 0
        ) {
            this.applyTotalWeight();
        }
        if (
            previous === 1 &&
            index !== 1 &&
            this.gleichesGewicht &&
            this.gleichesGewicht > 0 &&
            this.editableRowCount > 0
        ) {
            this.applyEqualWeight();
        }
        this.activeTabIndex = index;
    }

    onGleichesGewichtChanged(): void {
        if (this.isSyncing) {
            return;
        }
        if (!this.gleichesGewicht || this.gleichesGewicht <= 0 || this.editableRowCount <= 0) {
            return;
        }
        this.applyEqualWeight();
    }

    get editableRowCount(): number {
        return this.rows.filter(row => !row.hasExistingWeight).length;
    }

    close(): void {
        this.dialogRef.close(false);
    }

    save(): void {
        if (this.rows.length === 0) {
            this.dialogRef.close(true);
            return;
        }

        if (this.activeTabIndex === 0) {
            if (this.editableRowCount <= 0) {
                this.messageService.infoMessage('Alle Tiere haben bereits ein Gewicht. Bitte im Tab Einzeltier bearbeiten.');
            } else if (!this.totalGewicht || this.totalGewicht <= 0) {
                this.messageService.infoMessage('Bitte ein Gesamtgewicht größer als 0 eingeben.');
                return;
            }
            if (this.editableRowCount > 0) {
                this.applyTotalWeight();
            }
        } else if (this.activeTabIndex === 1) {
            if (this.editableRowCount <= 0) {
                this.messageService.infoMessage('Alle Tiere haben bereits ein Gewicht. Bitte im Tab Einzeltier bearbeiten.');
            } else if (!this.gleichesGewicht || this.gleichesGewicht <= 0) {
                this.messageService.infoMessage('Bitte ein Gewicht größer als 0 eingeben.');
                return;
            }
            if (this.editableRowCount > 0) {
                this.applyEqualWeight();
            }
        }

        const invalid = this.rows.some(row => !row.gewicht || row.gewicht <= 0);
        if (invalid) {
            this.messageService.infoMessage('Bitte für jedes Tier ein Gewicht größer als 0 eingeben.');
            return;
        }

        const entries = this.rows.map(row => ({
            gt_id: row.tier.gt_id,
            gewicht: Number(row.gewicht),
        }));
        for (const entry of entries) {
            this.einstallenService.setPendingGewicht(entry.gt_id, entry.gewicht);
            if (this.data?.scanErgebnisse?.length) {
                const scan = this.data.scanErgebnisse.find(s => s?.gt_id === entry.gt_id);
                if (scan) {
                    scan.einstallgewicht = entry.gewicht;
                }
            }
        }

        const gsId = Number(this.data?.gsId || this.einstallenService.getCurrentGruppe()?.gs_id || 0);
        if (gsId <= 0) {
            this.messageService.infoMessage('Gewichte vorgemerkt. Speicherung erfolgt beim Melden.');
            this.dialogRef.close(true);
            return;
        }

        this.isSaving = true;
        this.einstallenService.persistEinstallgewichte(entries, gsId).subscribe({
            next: () => {
                this.isSaving = false;
                this.messageService.infoMessage('Gewichte gespeichert.');
                this.dialogRef.close(true);
            },
            error: err => {
                this.isSaving = false;
                this.messageService.errorMessage('Gewichte konnten nicht gespeichert werden!', err);
            },
        });
    }

    get calculatedWeightFromTotal(): number {
        if (!this.totalGewicht || this.totalGewicht <= 0 || this.editableRowCount <= 0) {
            return 0;
        }
        return Number((this.totalGewicht / this.editableRowCount).toFixed(1));
    }

    getOhrmarke10(ohrmarke: string | null | undefined): string {
        const normalized = String(ohrmarke || '').replace(/\s/g, '');
        if (!normalized) {
            return '';
        }
        return normalized.length > 10 ? normalized.substring(normalized.length - 10) : normalized;
    }

    private applyTotalWeight(): void {
        const perTier = this.calculatedWeightFromTotal;
        this.isSyncing = true;
        this.rows.filter(row => !row.hasExistingWeight).forEach(row => {
            row.gewicht = perTier;
        });
        this.isSyncing = false;
        this.syncAggregatesFromRows();
    }

    private applyEqualWeight(): void {
        const value = Number(this.gleichesGewicht);
        this.isSyncing = true;
        this.rows.filter(row => !row.hasExistingWeight).forEach(row => {
            row.gewicht = value;
        });
        this.isSyncing = false;
        this.syncAggregatesFromRows();
    }

    private syncAggregatesFromRows(): void {
        const editableRows = this.rows.filter(row => !row.hasExistingWeight);
        const weights = editableRows
            .map(row => Number(row.gewicht))
            .filter(weight => !Number.isNaN(weight) && weight > 0);

        // Gesamtgewicht-Feld nicht überschreiben, solange der Nutzer dort tippt (Rundungs-Feedback-Schleife).
        if (this.activeTabIndex !== 0) {
            this.totalGewicht =
                weights.length > 0 ? Number(weights.reduce((sum, weight) => sum + weight, 0).toFixed(1)) : null;
        }

        const allRowsHaveWeight = weights.length === editableRows.length && editableRows.length > 0;
        const firstWeight = allRowsHaveWeight ? weights[0] : null;
        const areAllEqual = allRowsHaveWeight && weights.every(weight => Math.abs(weight - firstWeight) < 0.0001);
        if (this.activeTabIndex !== 1) {
            this.gleichesGewicht =
                areAllEqual && firstWeight !== null ? Number(firstWeight.toFixed(1)) : null;
        }
    }

    private loadRowsFromScans(scans: ScanErgebnis[]): void {
        const successScans = scans.filter(scan => !!scan?.success && !!scan?.gt_id && !scan?.bereitsInGruppe);
        const source = this.data?.onlyMissing
            ? successScans.filter(scan => !scan?.einstallgewicht || Number(scan.einstallgewicht) <= 0)
            : successScans;

        this.rows = source.map(scan => {
            const tier = new GruppeTier();
            tier.gt_id = scan.gt_id;
            tier.ohrmarke_notnull = scan.ohrmarke_notnull;
            tier.einstallgewicht = scan.einstallgewicht;
            const existingWeight = Number(scan?.einstallgewicht || 0) > 0;
            return {
                tier,
                gewicht:
                    this.einstallenService.getPendingGewichtForTier(scan?.gt_id) ??
                    scan?.einstallgewicht ??
                    null,
                hasExistingWeight: existingWeight,
            };
        });
        if (this.rows.length > 0) {
            this.gleichesGewicht = this.rows[0]?.gewicht || null;
        }
        this.syncAggregatesFromRows();
        this.isLoading = false;
    }
}
